import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Clock, ChevronRight, Loader2, Plus, Search,
  Tent as TentIcon,
} from 'lucide-react';
import WhatsNewWhisper from '../components/WhatsNewWhisper';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BASE CAMP — COORDINATION VIEW (HARD-LOCKED)
 *
 * Base Camp answers only: "what is happening, what can be joined, what can be created".
 * It contains NOTHING personal. Identity, participation, memory, invites, connections,
 * orgs, groups, commons, inbox, SCUTA, etc. all live in /backpack.
 *
 * Allowed on Base Camp:
 *   - Activity feed / carousel
 *   - Category filters
 *   - Time filters
 *   - Location context
 *   - Create Activity entry point
 *   - Map access
 *
 * Header exception (top-right only):
 *   - Profile avatar → /backpack (authed)
 *   - "Your Profile" button → /auth?mode=signin (guest)
 */

const CATEGORIES = [
  { id: null, label: 'All', icon: '📍' },
  { id: 'move', label: 'Move', icon: '🏃' },
  { id: 'outdoors', label: 'Outdoors', icon: '🌳' },
  { id: 'create', label: 'Create', icon: '🎨' },
  { id: 'play', label: 'Play', icon: '🎲' },
  { id: 'listen', label: 'Listen', icon: '🎤' },
  { id: 'learn', label: 'Learn', icon: '📖' },
  { id: 'gather', label: 'Gather', icon: '☕' },
  { id: 'care', label: 'Care', icon: '💛' },
  { id: 'serve', label: 'Serve', icon: '🤝' },
  { id: 'explore', label: 'Explore', icon: '🧭' },
];

const TIME_FILTERS = [
  { id: 'now', label: 'Now' },
  { id: 'today', label: 'Today' },
  { id: 'tonight', label: 'Tonight' },
  { id: 'week', label: 'This Week' },
];

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = d - now;
    if (diff > 0 && diff < 3600000) return `Starts in ${Math.round(diff / 60000)} min`;
    if (diff > 0 && diff < 86400000) {
      return d.toLocaleDateString('en-US', { weekday: 'short' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function deriveLiveState(act) {
  const count = act.participant_count ?? (act.participants || []).length;
  if (count >= 3) return { label: 'Good to go', color: '#059669' };
  if (count >= 1) return { label: `Needs ${3 - count} more`, color: '#D97706' };
  return { label: 'Needs people', color: '#D97706' };
}

function filterByTime(activities, timeFilter) {
  if (timeFilter === 'now') return activities;
  return activities.filter(a => {
    if (!a.start_time) return true;
    const now = new Date();
    const actTime = new Date(a.start_time);
    if (timeFilter === 'tonight') {
      const tonight = new Date(now); tonight.setHours(23, 59, 59, 999);
      const eveningStart = new Date(now); eveningStart.setHours(17, 0, 0, 0);
      return actTime >= (now.getHours() >= 17 ? now : eveningStart) && actTime <= tonight;
    }
    if (timeFilter === 'today') {
      const endOfDay = new Date(now); endOfDay.setHours(23, 59, 59, 999);
      return actTime <= endOfDay;
    }
    if (timeFilter === 'week') {
      const endOfWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      return actTime <= endOfWeek;
    }
    return true;
  });
}

export default function BaseCamp() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [timeFilter, setTimeFilter] = useState('now');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [hasPendingAsk, setHasPendingAsk] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFirstNudge, setShowFirstNudge] = useState(false);
  const carouselRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/activities/?limit=24`);
        if (mounted) {
          setActivities(res.data?.activities || []);
          setLoadError(false);
        }
      } catch {
        if (mounted) {
          setActivities([]);
          setLoadError(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Light profile lookup just for the header avatar (never displayed in content)
  useEffect(() => {
    if (!user) { setAvatarUrl(null); return; }
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/api/user/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAvatarUrl(res.data?.picture || res.data?.profile_image || null))
      .catch(() => {});
  }, [user]);

  // Sponsor avatar indicator — a quiet presence signal, nothing more.
  // Shows a small dot on the avatar only when the current user owns a sponsor
  // account AND has at least one pending support ask. No counts, no urgency.
  useEffect(() => {
    if (!user) { setHasPendingAsk(false); return; }
    let mounted = true;
    axios.get(`${API}/api/support/requests/presence`, { withCredentials: true })
      .then(res => {
        if (!mounted) return;
        const isSponsor = Boolean(res.data?.is_sponsor);
        const pending = Boolean(res.data?.has_pending);
        setHasPendingAsk(isSponsor && pending);
      })
      .catch(() => { if (mounted) setHasPendingAsk(false); });
    return () => { mounted = false; };
  }, [user]);

  // Gentle first-activity nudge: one-time, passive. If the user spends ~5 min
  // on Base Camp without any interaction, a single quiet line appears. It is
  // shown at most once per user (localStorage) and clears itself on the next
  // interaction. No overlay, no animation, no re-trigger.
  useEffect(() => {
    try {
      if (localStorage.getItem('lk_first_nudge_seen')) return;
    } catch { return; }
    let timer = null;
    let hideTimer = null;
    let cancelled = false;
    const events = ['mousedown', 'keydown', 'touchstart', 'wheel'];
    const cancel = () => {
      cancelled = true;
      if (timer) { clearTimeout(timer); timer = null; }
      events.forEach(e => window.removeEventListener(e, cancel, true));
    };
    const show = () => {
      if (cancelled) return;
      try { localStorage.setItem('lk_first_nudge_seen', '1'); } catch {}
      setShowFirstNudge(true);
      events.forEach(e => window.removeEventListener(e, cancel, true));
      // Quiet auto-dismiss on next interaction or after 30s.
      const dismiss = () => {
        setShowFirstNudge(false);
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        events.forEach(e => window.removeEventListener(e, dismiss, true));
      };
      events.forEach(e => window.addEventListener(e, dismiss, true));
      hideTimer = setTimeout(dismiss, 30000);
    };
    timer = setTimeout(show, 5 * 60 * 1000);
    events.forEach(e => window.addEventListener(e, cancel, true));
    return () => {
      cancel();
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const filtered = filterByTime(activities, timeFilter)
    .filter(a => !selectedCategory || a.category === selectedCategory)
    .filter(a => !searchTerm || (a.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || (typeof a.location === 'string' && a.location.toLowerCase().includes(searchTerm.toLowerCase())));

  const handleStartSomething = () => {
    if (user) navigate('/activities/create');
    else toast('Create a free account to start something', { action: { label: 'Sign up', onClick: () => navigate('/auth?mode=signup') } });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="base-camp">

      {/* HEADER — identity-free except for one top-right element */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <h1 className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <TentIcon className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              Base Camp
            </h1>
            {user ? (
              <div className="relative">
                <button
                  onClick={() => navigate('/backpack')}
                  className="w-9 h-9 rounded-full overflow-hidden hover:opacity-80 transition-opacity block"
                  style={{ border: '2px solid var(--lk-sage)' }}
                  data-testid="header-profile-btn"
                  aria-label="Open Backpack"
                >
                  <img
                    src={avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=6B8F71&color=fff&size=36`}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=U&background=6B8F71&color=fff&size=36`; }}
                  />
                </button>
                {hasPendingAsk && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigate('/support/requests'); }}
                    aria-label="Support asks waiting"
                    data-testid="sponsor-inbox-indicator"
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full focus:outline-none"
                    style={{
                      backgroundColor: 'var(--lk-sage)',
                      opacity: 0.75,
                      border: '1.5px solid var(--fy-surface-card)',
                      padding: 0,
                    }}
                  />
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/auth?mode=signin')}
                className="h-8 px-4 rounded-full text-xs font-semibold"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
                data-testid="guest-profile-btn"
              >
                Your Profile
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto pb-24">

        {/* SUPPORTING LINE */}
        <div className="px-4 mt-5">
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>See what's happening nearby.</p>
        </div>

        {/* LOCATION CONTEXT */}
        <div className="px-4 mt-3 flex items-center justify-between" data-testid="location-context">
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--lk-sage)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Burlington, Vermont</span>
          </div>
          <button
            onClick={() => navigate('/settings/location')}
            className="text-xs font-medium"
            style={{ color: 'var(--lk-sage)' }}
            data-testid="change-location-btn"
          >
            Change
          </button>
        </div>

        {/* TIME FILTERS */}
        <div className="px-4 mt-3 flex items-center gap-1.5" data-testid="time-toggle">
          {TIME_FILTERS.map(t => (
            <button
              key={t.id}
              onClick={() => setTimeFilter(t.id)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
              style={{
                backgroundColor: timeFilter === t.id ? 'var(--lk-sage)' : 'var(--fy-surface-card)',
                color: timeFilter === t.id ? '#fff' : 'var(--fy-text-secondary)',
                border: `1px solid ${timeFilter === t.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
              }}
              data-testid={`filter-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* SEARCH — placed above category pills */}
        <div className="px-4 mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search activities..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
              data-testid="basecamp-search-input"
            />
          </div>
        </div>

        {/* CATEGORY ROW */}
        <div className="relative mt-4" data-testid="category-row">
          <div
            className="flex gap-2 overflow-x-auto px-4 pb-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {CATEGORIES.map(cat => (
              <button
                key={cat.id || 'all'}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0 text-xs font-medium transition-colors"
                style={{
                  backgroundColor: selectedCategory === cat.id ? 'var(--lk-sage)' : 'var(--fy-surface-card)',
                  color: selectedCategory === cat.id ? '#fff' : 'var(--fy-text-secondary)',
                  border: `1px solid ${selectedCategory === cat.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                }}
                data-testid={`cat-${cat.id || 'all'}`}
              >
                <span className="text-sm">{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>
          <div
            className="absolute right-0 top-0 bottom-1 w-8 pointer-events-none"
            style={{ background: 'linear-gradient(to left, var(--fy-background-primary), transparent)' }}
          />
        </div>

        {/* WHAT'S NEW — calm, dismissible, 7-day max. Authed users only. */}
        <WhatsNewWhisper user={user} />

        {/* SECTION LABEL — extra breath above to decompress the filter cluster from the feed */}
        <div className="px-4 mt-10 mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--fy-text-disabled)' }} data-testid="happening-now-title">
            Happening now
          </p>
        </div>

        {/* ACTIVITY CAROUSEL */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--fy-text-disabled)' }} />
          </div>
        ) : loadError ? (
          <div className="px-4" data-testid="load-error-whisper">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              Couldn't load right now. Try again in a moment.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4" data-testid="empty-state">
            <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
              Nothing here yet.
            </p>
            <p className="text-sm mt-1 mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              You could be the first this week.
            </p>
            <button
              onClick={handleStartSomething}
              className="text-sm font-medium"
              style={{ color: 'var(--lk-sage)' }}
              data-testid="empty-start-btn"
            >
              Start something
            </button>
          </div>
        ) : (
          <div
            ref={carouselRef}
            className="flex gap-3 overflow-x-auto px-4 pb-2"
            style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            data-testid="activity-carousel"
          >
            {filtered.map(act => {
              const state = deriveLiveState(act);
              const locationStr = typeof act.location === 'string' ? act.location : (act.location?.name || '');
              return (
                <div
                  key={act.activity_id}
                  className="flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                  style={{ width: '280px', scrollSnapAlign: 'start', backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                  onClick={() => navigate(`/activities/${act.activity_id}`)}
                  data-testid={`card-${act.activity_id}`}
                >
                  <div className="w-full h-28 overflow-hidden" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                    {act.image_url ? (
                      <img
                        src={act.image_url}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, var(--fy-surface-muted) 0%, #E4EADD 100%)' }}
                      >
                        <MapPin className="w-5 h-5" style={{ color: 'var(--lk-sage)', opacity: 0.35 }} />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold line-clamp-2 mb-1.5" style={{ color: 'var(--fy-text-primary)' }}>
                      {act.title}
                    </p>
                    {locationStr && (
                      <p className="text-xs flex items-center gap-1 mb-1" style={{ color: 'var(--fy-text-secondary)' }}>
                        <MapPin className="w-3 h-3 flex-shrink-0" />{locationStr.length > 30 ? locationStr.slice(0, 30) + '...' : locationStr}
                      </p>
                    )}
                    {act.start_time && (
                      <p className="text-xs flex items-center gap-1 mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
                        <Clock className="w-3 h-3 flex-shrink-0" />{formatTime(act.start_time)}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${state.color}15`, color: state.color }}>
                        {state.label}
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'var(--lk-sage)' }}>
                        View
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ACTION PATHWAYS — Create + Map + Search */}
        <div className="px-4 mt-8 space-y-2">
          {showFirstNudge && (
            <p
              className="text-xs mb-1"
              style={{ color: 'var(--fy-text-disabled)' }}
              data-testid="first-activity-nudge"
            >
              You can also just start something.
            </p>
          )}
          <button
            onClick={handleStartSomething}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-transform active:scale-[0.99]"
            style={{ backgroundColor: 'var(--lk-sage)', border: '1px solid var(--lk-sage)' }}
            data-testid="start-something-btn"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <Plus className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">Start something</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.8)' }}>Create a new activity</p>
            </div>
            <ChevronRight className="w-4 h-4 text-white" />
          </button>

          <button
            onClick={() => navigate('/map')}
            className="w-full flex items-center gap-3 p-3 rounded-xl text-left"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="basecamp-map-btn"
          >
            <MapPin className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
            <div className="flex-1">
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>Open Map</p>
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>See what's around you</p>
            </div>
            <ChevronRight className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
          </button>
        </div>

        {/* GUEST-ONLY FOOTER CTA */}
        {!user && (
          <div className="px-4 mt-10 text-center">
            <button
              onClick={() => navigate('/auth?mode=signup')}
              className="h-10 px-6 rounded-full text-sm font-semibold"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
              data-testid="guest-signup-btn"
            >
              Create Account
            </button>
          </div>
        )}

      </main>
    </div>
  );
}
